import React, {Component} from "react";
import {Button, ButtonColors, ButtonVariants, Modal, Text} from "@clockout-dev/design-elements";
import UploadsSimulatorController from "./uploads-simulator.controller";
import withHooks from "../../core/withHooks";
import Inherit from "../../core/inherit";
import Page from "../common/page/Page";
import SplitLayout from "../common/layouts/splitLayout/SplitLayout";
import {SCOPES} from "../../constants/scopes.constants";
import SorPresetBar from "./elements/SorPresetBar";
import SorComposer from "./elements/SorComposer";
import UploadRunPanel from "./elements/UploadRunPanel";
import RunProgress from "./elements/RunProgress";
import ResultsPanel from "./elements/ResultsPanel";
import DocumentAuditPanel from "./elements/audit/DocumentAuditPanel";
import MemberSorModal from "./elements/MemberSorModal";
import {AUDIT_TAB_DESCRIPTION, AUDIT_TAB_KEY, DEFAULT_TAB, MODES, SIMULATOR_TABS} from "./uploads-simulator.constants";
import {buildDefaultSor, validateSor} from "./sor.model";
import styles from "./styles/uploads-simulator.module.scss";

class UploadsSimulator extends Inherit(UploadsSimulatorController, Component) {
    state = {
        activeTab: DEFAULT_TAB,
        // Single shared SOR across all tabs — switching modes keeps the
        // authored data (only the selected files reset per mode).
        sor: buildDefaultSor(),
        sorErrors: {},
        presetNotes: [],
        presets: [],
        selectedPresetName: null,
        samplePresetLoading: false,
        files: [],
        // Remount key for DragAndDrop (it owns its file list internally).
        dndKey: 0,
        // Run lifecycle: `run` is non-null while uploading/polling, then the
        // outcome lands in `report` (completed) or `runError` (anything else).
        run: null,
        report: null,
        runError: null,
        selectedFileIndex: 0,
        // Maintained by `ResponsiveController` (mixed in via `Inherit`).
        // Declared here because component field declarations run after the
        // mixin's constructor and would overwrite any initial value.
        isMobile: false,
        isTablet: false,
        // Below 1024px the results render in a full-screen modal that
        // auto-opens when a run finishes (clone of reports' panelOpenMobile).
        panelOpenMobile: false,
        // ---- Document Audit tab (member search + real intake jobs) ----
        memberQuery: "",
        memberResults: [],
        memberSearchLoading: false,
        selectedMember: null,
        jobs: [],
        jobsLoading: false,
        jobsError: null,
        selectedJobId: null,
        // Object URL held by the controller (`_previewObjectUrl`) and revoked
        // on every selection change + cleanup.
        preview: {objectUrl: null, mimeType: null, loading: false, error: null},
        // ---- "Load from member…" (composer) ----
        memberSorModalOpen: false,
        memberSorLoading: false,
        memberSorSource: null
    };

    // ---------- renderers ---------------------------------------------------

    renderComposerColumn(validationMessages, canRun) {
        const {activeTab, sor, sorErrors, presetNotes, presets, selectedPresetName, samplePresetLoading, memberSorSource, files, dndKey} = this.state;

        return (
            <div className={styles.composerColumn}>
                <SorPresetBar
                    presets={presets}
                    selectedPresetName={selectedPresetName}
                    sampleLoading={samplePresetLoading}
                    sourceLabel={memberSorSource ? `Member SOR — ${memberSorSource.memberName || memberSorSource.clientId} (unsaved)` : null}
                    onSelectPreset={this.handleSelectPreset.bind(this)}
                    onClearPreset={() => this.setSelectedPresetName(null)}
                    onSavePreset={this.handleSavePreset.bind(this)}
                    onSaveAsPreset={this.handleSaveAsPreset.bind(this)}
                    onDeletePreset={this.handleDeletePreset.bind(this)}
                    onLoadSample={() => this.loadServerPreset(activeTab)}
                    onLoadFromMember={this.openMemberSorModal.bind(this)}
                    onResetSor={this.handleResetSor.bind(this)}
                />
                <UploadRunPanel
                    modeConfig={MODES[activeTab]}
                    files={files}
                    dndKey={dndKey}
                    validationMessages={validationMessages}
                    canRun={canRun}
                    running={this.isRunning}
                    canSimulate={this.canSimulate}
                    onFilesChange={this.handleFilesChange.bind(this)}
                    onRun={this.runSimulation.bind(this)}
                />
                <SorComposer mode={activeTab} sor={sor} errors={sorErrors} notes={presetNotes} onSectionChange={this.handleSorSectionChange.bind(this)} />
            </div>
        );
    }

    renderResults() {
        const {run, report, runError, selectedFileIndex} = this.state;

        return (
            <ResultsPanel
                run={run}
                report={report}
                runError={runError}
                selectedFileIndex={selectedFileIndex}
                onSelectFile={this.setSelectedFileIndex.bind(this)}
                onCancelRun={this.cancelRun.bind(this)}
                onCopyJson={this.handleCopyJson.bind(this)}
            />
        );
    }

    renderAuditPanel() {
        const {memberQuery, memberResults, memberSearchLoading, selectedMember, jobs, jobsLoading, jobsError, selectedJobId, preview, memberSorLoading} = this.state;

        return (
            <DocumentAuditPanel
                memberQuery={memberQuery}
                memberResults={memberResults}
                memberSearchLoading={memberSearchLoading}
                selectedMember={selectedMember}
                jobs={jobs}
                jobsLoading={jobsLoading}
                jobsError={jobsError}
                selectedJobId={selectedJobId}
                preview={preview}
                memberSorLoading={memberSorLoading}
                onSearchMembers={this.handleMemberSearch.bind(this)}
                onSelectMember={this.selectMember.bind(this)}
                onSelectJob={this.selectJob.bind(this)}
                onLoadMemberSor={this.handleLoadMemberSorIntoComposer.bind(this)}
                onCopyText={this.handleCopyText.bind(this)}
            />
        );
    }

    renderSimulationContent() {
        const {sor, activeTab, files, run, report, runError, panelOpenMobile} = this.state;
        const isSmallScreen = this.isSmallScreen;

        // Live gate for the Run button; field-level errors are only written
        // into `sorErrors` (composer highlights) on an actual run attempt.
        const sorValidation = validateSor(sor, activeTab);
        const fileMessages = this.getFileValidationMessages();
        const sorIssueCount = Object.keys(sorValidation.errors).length;
        const validationMessages = [...fileMessages, ...(sorIssueCount ? [`${sorIssueCount} SOR issue${sorIssueCount === 1 ? "" : "s"} — press Run to highlight them in the composer.`] : [])];
        const canRun = this.canSimulate && sorValidation.ok && fileMessages.length === 0 && files.length > 0;

        return (
            <SplitLayout
                key="uploads-simulator"
                embedded
                className={styles.splitRoot}
                contentClassName={styles.splitContent}
                sideClassName={styles.splitSide}
                side={!isSmallScreen ? this.renderResults() : null}>
                {this.renderComposerColumn(validationMessages, canRun)}
                {isSmallScreen && run ? (
                    <div className={styles.mobileProgress}>
                        <RunProgress run={run} onCancel={this.cancelRun.bind(this)} />
                    </div>
                ) : null}
                {isSmallScreen && !run && (report || runError) && !panelOpenMobile ? (
                    <Button variant={ButtonVariants.PRIMARY} color={ButtonColors.SECONDARY} className={styles.viewResultsButton} onClick={() => this.setPanelOpenMobile(true)}>
                        <Text size="xs" fontWeight="semibold">
                            View simulation results
                        </Text>
                    </Button>
                ) : null}
            </SplitLayout>
        );
    }

    render() {
        const {activeTab, report, runError, panelOpenMobile, memberSorModalOpen, memberSorLoading, memberQuery, memberResults, memberSearchLoading} = this.state;
        const isAudit = activeTab === AUDIT_TAB_KEY;

        return (
            <Page
                title={{text: "Manual Uploads", highlight: "Simulator"}}
                description={isAudit ? AUDIT_TAB_DESCRIPTION : MODES[activeTab]?.description}
                scope={SCOPES.UPLOADS_SIMULATOR_RUN}
                tabs={SIMULATOR_TABS}
                activeTabKey={activeTab}
                onTabChange={key => this.handleTabChange(key)}
                notificationContextHolder={this.props.notificationContextHolder}>
                {this.props.contextHolder}
                {/* `audit` is not a simulation mode — it swaps the whole
                    composer/results split for the document-audit layout. */}
                <div className={styles.contentWrapper}>{isAudit ? this.renderAuditPanel() : this.renderSimulationContent()}</div>

                {/* Mobile / tablet: the results panel opens as a full-screen
                    modal when a run finishes (report or error). */}
                {!isAudit ? (
                    <Modal
                        open={panelOpenMobile && !!(report || runError)}
                        onCancel={this.handleClosePanel.bind(this)}
                        footer={null}
                        title={null}
                        className={styles.panelModalOverride}
                        destroyOnHidden
                        width="100%">
                        {this.renderResults()}
                    </Modal>
                ) : null}

                {/* "Load from member…" — available from the preset bar on any
                    composer tab (and reused by the audit rail button flow). */}
                <MemberSorModal
                    open={memberSorModalOpen}
                    loading={memberSorLoading}
                    query={memberQuery}
                    results={memberResults}
                    searchLoading={memberSearchLoading}
                    onSearch={this.handleMemberSearch.bind(this)}
                    onSelect={this.handleMemberSorModalPick.bind(this)}
                    onClose={() => this.setMemberSorModalOpen(false)}
                />
            </Page>
        );
    }
}

export default withHooks(UploadsSimulator);
